import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography
} from '@customink/pigment-react';
import {CircularProgress} from '@mui/material';
import React, {useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {toMmsUrl, withParams} from '../../../../utils/imageUtils';
import useImageLoading from '../hooks/useImageLoading';
import {getEffectiveHash} from '../utils/categoryImageParser';
import {buildEditableParams} from '../utils/parameterUtils';

export default function ConfirmationDialog({
  categoryImages,
  colors,
  onCancel,
  onConfirm,
  open,
  previewUrl,
  primaryCategoryId,
  styleCardImageUrl
}) {
  const {formState, getValues} = useFormContext();
  const {isSubmitting} = formState;
  const formValues = getValues();
  const {useLabImage} = formValues;

  // Generate URLs for dual display when lab image is disabled
  const {colorImageUrl, laydownImageUrl} = useMemo(() => {
    if (!formValues.useLabImage && styleCardImageUrl) {
      // Use first color if colorId is null/empty, otherwise use specified colorId
      let effectiveColorId = formValues.colorId;

      // If no colorId is specified (null or empty), use the first available color
      if (!effectiveColorId && colors?.[0]?.id != null) {
        effectiveColorId = String(colors[0].id);
      }

      if (effectiveColorId) {
        // Generate color image URL with all image processing parameters
        const hash = getEffectiveHash(
          styleCardImageUrl,
          categoryImages,
          primaryCategoryId
        );

        const colorImagePath = `mms/images/catalog/${hash}/colors/${effectiveColorId}/views/alt/front_medium_extended.png`;
        const params = buildEditableParams(formValues);
        const colorUrl = withParams(toMmsUrl(colorImagePath), params);

        // Laydown image URL (unchanged, no processing parameters)
        const laydownUrl = toMmsUrl(styleCardImageUrl);

        return {
          colorImageUrl: colorUrl,
          laydownImageUrl: laydownUrl
        };
      }
    }

    return {colorImageUrl: null, laydownImageUrl: null};
  }, [
    formValues.useLabImage,
    formValues.colorId,
    formValues,
    styleCardImageUrl,
    categoryImages,
    colors,
    primaryCategoryId
  ]);

  // Use stable loading hooks to prevent flickering
  const labImageLoading = useImageLoading(previewUrl);
  const colorImageLoading = useImageLoading(colorImageUrl);
  const laydownImageLoading = useImageLoading(laydownImageUrl);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="Confirm Algolia Update"
      width="lg"
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0.125rem'
        }
      }}>
      <DialogContent>
        <Stack spacing={3}>
          <Typography color="text.secondary">
            You are about to update the Algolia image attributes for this style.
            These changes will be <strong>live immediately</strong> and will
            affect how the style appears on product listing pages and search
            results.
          </Typography>
          <Typography color="text.secondary">
            <strong>Important:</strong> This images will be used as a preview
            for the style on product listing pages and search results.
          </Typography>

          {/* Image Preview */}
          <Box
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: '0.0625rem',
              p: '1rem'
            }}>
            <Typography variant="subtitle2" gutterBottom>
              Image Preview
            </Typography>
            <Divider sx={{mb: '1rem'}} />

            <Box
              sx={{
                alignItems: formValues.useLabImage ? 'center' : 'stretch',
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '0.0625rem',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '24.375rem',
                overflow: 'hidden',
                padding: formValues.useLabImage ? 0 : '0.5rem',
                position: 'relative',
                width: '100%'
              }}>
              {/* Lab Image Mode - Single Image */}
              {formValues.useLabImage && (
                <>
                  {labImageLoading.imageLoading && (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{height: '100%', width: '100%'}}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary">
                        Loading preview...
                      </Typography>
                    </Stack>
                  )}

                  {labImageLoading.imageError && (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{height: '100%', width: '100%'}}>
                      <Typography variant="body2" color="error">
                        ⚠️ Preview unavailable
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Image may still be valid
                      </Typography>
                    </Stack>
                  )}

                  {previewUrl && (
                    <img
                      key={`lab-${previewUrl}`}
                      ref={labImageLoading.imageRefCallback}
                      src={previewUrl}
                      alt="Lab preview"
                      style={{
                        display:
                          labImageLoading.imageLoading ||
                          labImageLoading.imageError
                            ? 'none'
                            : 'block',
                        height: '26rem',
                        objectFit: 'contain',
                        width: 'auto'
                      }}
                      onLoad={labImageLoading.handleImageLoad}
                      onError={labImageLoading.handleImageError}
                    />
                  )}
                </>
              )}

              {/* Standard Mode - Dual Images */}
              {!formValues.useLabImage && colorImageUrl && laydownImageUrl && (
                <Stack
                  direction={{md: 'row', xs: 'column'}}
                  spacing={1}
                  sx={{
                    height: {md: 'calc(24.375rem - 1rem)', xs: 'auto'},
                    maxWidth: '50rem',
                    mx: 'auto',
                    width: '100%'
                  }}>
                  {/* Initial Product Image (Laydown) */}
                  <Box sx={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                    <Typography
                      variant="subtitle2"
                      sx={{mb: '0.5rem', textAlign: 'center'}}>
                      Initial Product Image
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '0.0625rem',
                        flex: 1,
                        minHeight: {md: '20.3125rem', xs: '16.25rem'},
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                      <img
                        key={`laydown-${laydownImageUrl}`}
                        ref={laydownImageLoading.imageRefCallback}
                        src={laydownImageUrl}
                        alt="Laydown preview"
                        style={{
                          display:
                            laydownImageLoading.imageLoading ||
                            laydownImageLoading.imageError
                              ? 'none'
                              : 'block',
                          height: '100%',
                          objectFit: 'contain',
                          width: '100%'
                        }}
                        onLoad={laydownImageLoading.handleImageLoad}
                        onError={laydownImageLoading.handleImageError}
                      />

                      {laydownImageLoading.imageLoading && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: 0,
                            width: '100%'
                          }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}

                      {laydownImageLoading.imageError && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            position: 'absolute',
                            textAlign: 'center',
                            top: 0,
                            width: '100%'
                          }}>
                          <Typography variant="caption" color="error">
                            Failed to load
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Color Image */}
                  <Box sx={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                    <Typography
                      variant="subtitle2"
                      sx={{mb: '0.5rem', textAlign: 'center'}}>
                      Color Image (Image settings preview)
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '0.0625rem',
                        flex: 1,
                        minHeight: {md: '20.3125rem', xs: '16.25rem'},
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                      <img
                        key={`color-${colorImageUrl}`}
                        ref={colorImageLoading.imageRefCallback}
                        src={colorImageUrl}
                        alt="Color preview"
                        style={{
                          display:
                            colorImageLoading.imageLoading ||
                            colorImageLoading.imageError
                              ? 'none'
                              : 'block',
                          height: '100%',
                          objectFit: 'contain',
                          width: '100%'
                        }}
                        onLoad={colorImageLoading.handleImageLoad}
                        onError={colorImageLoading.handleImageError}
                      />

                      {colorImageLoading.imageLoading && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: 0,
                            width: '100%'
                          }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}

                      {colorImageLoading.imageError && (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            position: 'absolute',
                            textAlign: 'center',
                            top: 0,
                            width: '100%'
                          }}>
                          <Typography variant="caption" color="error">
                            Failed to load
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Stack>
              )}

              {/* No preview available */}
              {!formValues.useLabImage &&
                (!colorImageUrl || !laydownImageUrl) && (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{height: '100%', width: '100%'}}>
                    <Typography variant="body2" color="text.secondary">
                      📷 Preview configuration incomplete
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Complete the form to see preview
                    </Typography>
                  </Stack>
                )}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: '0.0625rem',
              p: '1rem'
            }}>
            <Typography variant="subtitle2" gutterBottom>
              Configuration Summary
            </Typography>
            <Divider sx={{mb: '1rem'}} />

            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Initial Product Image:
                </Typography>
                <Typography variant="body1">
                  {useLabImage ? '🧪 Lab Image' : '👕 Laydown image'}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{p: '1.875rem'}}>
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="tertiary"
          onClick={onConfirm}
          disabled={isSubmitting}
          sx={{minWidth: '7.5rem'}}>
          {isSubmitting ? 'Updating...' : 'Confirm Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
